import { ApiBaseUrl } from './api';
import { ApiError } from '../utils/fetchers';
import { TiloGame, TiloPlayer, TiloTeam, TimeLeft } from '../types/tilo/tilo-game';

const TiloWebGlUrl =
  window?._env_?.REACT_APP_TILO_WEBGL_URL || process.env.REACT_APP_TILO_WEBGL_URL || '';
const TiloImagesUrl =
  window?._env_?.REACT_APP_TILO_IMAGES_URL || process.env.REACT_APP_TILO_IMAGES_URL || '';

/**
 * Converts a total number of seconds into a broken-down TimeLeft object.
 * @param totalSeconds - Total seconds remaining (clamped to 0).
 * @returns Zero-padded day, hour, min, sec strings.
 */
export function formatTime(totalSeconds: number): TimeLeft {
  const s = Math.floor(Math.max(0, totalSeconds));
  const day = Math.floor(s / 86400);
  const hour = Math.floor(s / 3600) - day * 24;
  const min = Math.floor(s / 60) - day * 1440 - hour * 60;
  const sec = s - day * 86400 - hour * 3600 - min * 60;
  return {
    day: String(day).padStart(2, '0'),
    hour: String(hour).padStart(2, '0'),
    min: String(min).padStart(2, '0'),
    sec: String(sec).padStart(2, '0')
  };
}

/**
 * Transforms raw TILO server game data into a typed TiloGame object.
 * Unwraps Mongoose-populated team and player references, computes derived
 * player counts, and constructs the participant URL from environment config.
 * @param raw - Raw JSON response from the TILO server.
 * @returns Processed and typed game object ready for the UI.
 */
export function processRawGame(raw: any): TiloGame {
  const teams: TiloTeam[] = (raw.teams ?? []).map((t: any) => {
    const team = t.team ?? t;
    const players: TiloPlayer[] = (team.players ?? []).map((p: any) => p.player ?? p);
    return {
      ...team,
      players,
      playedPlayers: players.filter((p) => p.username).length,
      takenPlayers: players.filter((p) => p.taken).length,
      connectedPlayers: players.filter((p) => p.state).length
    };
  });

  return {
    ...raw,
    teams,
    startedAt: raw.started ? new Date(raw.startedAt) : 'Not started yet',
    finishedAt: raw.finished ? new Date(raw.finishedAt) : 'Not finished yet',
    scoresHidden: raw.hideScore ?? false,
    URL: TiloWebGlUrl ? `${TiloWebGlUrl}/#/game/${raw._id}` : '',
    logoURL: raw.logo?.filename
      ? `${TiloImagesUrl}/users/logo/${raw.logo.filename}`
      : ''
  };
}

/**
 * Performs a GET request against the backend TILO proxy.
 * @param path - API path relative to the v2 base URL (e.g. 'tilo/games/123').
 * @returns Parsed JSON response, or null if the response body is empty.
 * @throws ApiError when the response status is not ok.
 */
export async function tiloGet(path: string) {
  const res = await fetch(`${ApiBaseUrl}/${path}`);
  let data: any;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) throw new ApiError(data?.message ?? res.statusText, res.status, data);
  return data;
}

/**
 * Performs a POST request against the backend TILO proxy.
 * @param path - API path relative to the v2 base URL (e.g. 'tilo/games/123/start').
 * @param body - Optional JSON request body (defaults to empty object).
 * @returns Parsed JSON response.
 * @throws ApiError when the response status is not ok.
 */
export async function tiloPost(path: string, body: object = {}) {
  const res = await fetch(`${ApiBaseUrl}/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  });
  let data: any;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) throw new ApiError(data?.message ?? res.statusText, res.status, data);
  return data;
}